import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { propertyAdEditorProvider } from '../../providers/propertyAdEditorProvider';
import type { PropertyAdInProgressWorkspace } from '../../types/propertyAdEditor';
import type { ResponseType } from '../../types/responses';

import { Loader, MyButton } from '../uiComponents';
import { ExistingInProgressDialog } from './ExistingInProgressDialog';
import { PropertyAdCreationMethod } from './PropertyAdCreationMethod';
import { PropertyAdEditor } from './PropertyAdEditor';

type EntryState =
  | { status: 'loading' }
  | { status: 'error'; message: string; retryAction: EntryRetryAction }
  | { status: 'existing'; workspace: PropertyAdInProgressWorkspace }
  | { status: 'methodSelection' }
  | { status: 'editor'; workspace: PropertyAdInProgressWorkspace };

type EntryRetryAction = 'lookup' | 'create' | 'reset' | 'recover';

const getErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

const getResponseErrorMessage = <T,>(response: ResponseType<T>, fallback: string): string =>
  response.message || fallback;

export const CreatePropertyAdV2 = () => {
  const [entryState, setEntryState] = useState<EntryState>({ status: 'methodSelection' });
  const isMountedRef = useRef(true);
  const requestInFlightRef = useRef(false);

  const runRequest = useCallback(async (request: () => Promise<void>) => {
    if (requestInFlightRef.current) return;

    requestInFlightRef.current = true;
    setEntryState({ status: 'loading' });

    try {
      await request();
    } finally {
      requestInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const recoverAfterCreateConflict = async () => {
    try {
      const response = await propertyAdEditorProvider.getInProgressWorkspace();

      if (!isMountedRef.current) return;

      if (response.ok && response.data) {
        setEntryState({ status: 'existing', workspace: response.data });
        return;
      }

      setEntryState({
        status: 'error',
        message: getResponseErrorMessage(response, 'Unable to recover the listing workspace'),
        retryAction: 'recover',
      });
    } catch (error) {
      if (!isMountedRef.current) return;

      setEntryState({
        status: 'error',
        message: getErrorMessage(error, 'Unable to recover the listing workspace'),
        retryAction: 'recover',
      });
    }
  };

  const createWorkspace = async () => {
    try {
      const response = await propertyAdEditorProvider.createInProgressWorkspace();

      if (!isMountedRef.current) return;

      if (response.ok && response.data) {
        setEntryState({ status: 'editor', workspace: response.data });
        return;
      }

      if (response.status === 409) {
        await recoverAfterCreateConflict();
        return;
      }

      setEntryState({
        status: 'error',
        message: getResponseErrorMessage(response, 'Unable to start the listing workspace'),
        retryAction: 'create',
      });
    } catch (error) {
      if (!isMountedRef.current) return;

      setEntryState({
        status: 'error',
        message: getErrorMessage(error, 'Unable to start the listing workspace'),
        retryAction: 'create',
      });
    }
  };

  const loadWorkspace = async () => {
    try {
      const response = await propertyAdEditorProvider.getInProgressWorkspace();

      if (!isMountedRef.current) return;

      if (!response.ok) {
        setEntryState({
          status: 'error',
          message: getResponseErrorMessage(response, 'Unable to load the listing workspace'),
          retryAction: 'lookup',
        });
        return;
      }

      if (response.data) {
        setEntryState({ status: 'existing', workspace: response.data });
        return;
      }

      await createWorkspace();
    } catch (error) {
      if (!isMountedRef.current) return;

      setEntryState({
        status: 'error',
        message: getErrorMessage(error, 'Unable to load the listing workspace'),
        retryAction: 'lookup',
      });
    }
  };

  const resetWorkspace = async () => {
    try {
      const response = await propertyAdEditorProvider.resetInProgressWorkspace();

      if (!isMountedRef.current) return;

      if (response.ok && response.data) {
        setEntryState({ status: 'editor', workspace: response.data });
        return;
      }

      setEntryState({
        status: 'error',
        message: getResponseErrorMessage(response, 'Unable to reset the listing workspace'),
        retryAction: 'reset',
      });
    } catch (error) {
      if (!isMountedRef.current) return;

      setEntryState({
        status: 'error',
        message: getErrorMessage(error, 'Unable to reset the listing workspace'),
        retryAction: 'reset',
      });
    }
  };

  const handleRetry = () => {
    if (entryState.status !== 'error') return;

    const retryRequest = {
      lookup: loadWorkspace,
      create: createWorkspace,
      reset: resetWorkspace,
      recover: recoverAfterCreateConflict,
    }[entryState.retryAction];

    void runRequest(retryRequest);
  };

  if (entryState.status === 'loading') {
    return (
      <Stack
        alignItems='center'
        aria-label='Loading listing workspace'
        justifyContent='center'
        minHeight={280}
        padding='32px 16px'
        role='status'
        width='100%'
      >
        <Loader />
      </Stack>
    );
  }

  if (entryState.status === 'error') {
    return (
      <Stack
        alignItems='center'
        component='section'
        justifyContent='center'
        minHeight={280}
        padding='32px 16px'
        role='alert'
        textAlign='center'
        width='100%'
      >
        <Typography color='#2a3842' component='h1' fontSize={22} variant='h5'>
          Unable to open PropertyAd Editor
        </Typography>
        <Typography
          color='#6f7d87'
          component='p'
          fontWeight={400}
          margin='10px 0 20px'
          maxWidth={520}
        >
          {entryState.message}
        </Typography>
        <MyButton
          data={{
            buttonName: 'Retry',
            buttonType: 'button',
            customWidth: '120px',
            variant: 'contained',
          }}
          onClick={handleRetry}
        />
      </Stack>
    );
  }

  if (entryState.status === 'methodSelection') {
    return <PropertyAdCreationMethod onManual={() => void runRequest(loadWorkspace)} />;
  }

  if (entryState.status === 'editor') {
    return <PropertyAdEditor workspace={entryState.workspace} />;
  }

  return (
    <Box minWidth={0} width='100%'>
      <ExistingInProgressDialog
        onContinue={(workspace) => setEntryState({ status: 'editor', workspace })}
        onStartNew={() => void runRequest(resetWorkspace)}
        workspace={entryState.workspace}
      />
    </Box>
  );
};
